import {agentViewQueueDisplayGroupValidator} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/agentViewWorkspaceCapability/domain/AgentView";
import {AMARON__EVENT_CONTAINER__LEAD_CATEGORY__COLUMN_ID} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/amaron/EventContainers";
import type {AgentViewQueueDisplayGroup} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/agentViewWorkspaceCapability/domain/AgentView";
import {z as zod} from "zod";
import type {Integer, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {
    ottopilotEventContainerWithEventsValidator,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import {columnValuesFromApiZodValidator} from "~/utilities/columnValuesFromApi";
import type {PlutoComplexFilter} from "~/utilities/queries";

/** Queue grouping is computed from current disposition, not the physical business category.
 * Move the viewer's single AND category selector into the backend display-group parameter.
 * All other filters (including OR/multi-value category expressions) retain their original semantics.
 */
export function resolveAgentViewQueueFilters(complexFilters: PlutoComplexFilter | undefined): {complexFilters?: PlutoComplexFilter; leadCategory?: AgentViewQueueDisplayGroup} {
    if (complexFilters === undefined || complexFilters.logic !== "AND") {
        return {complexFilters};
    }
    const flattenAnd = (filter: PlutoComplexFilter): PlutoComplexFilter["filters"] => filter.filters.flatMap(item =>
        "logic" in item && item.logic === "AND" ? flattenAnd(item) : [item]);
    const conjuncts = flattenAnd(complexFilters);
    const candidates = conjuncts.filter(filter => "column" in filter && filter.column === String(AMARON__EVENT_CONTAINER__LEAD_CATEGORY__COLUMN_ID));
    const candidate = candidates.length === 1 ? candidates[0] : undefined;
    if (candidate === undefined || !("column" in candidate)) {
        return {complexFilters};
    }
    const raw = candidate.operator === "equals" ? candidate.value : candidate.operator === "in" && Array.isArray(candidate.value) && candidate.value.length === 1 ? candidate.value[0] : undefined;
    const group = agentViewQueueDisplayGroupValidator.safeParse(raw);
    if (!group.success) {
        return {complexFilters};
    }
    const remaining = conjuncts.filter(filter => filter !== candidate);
    return {leadCategory: group.data, complexFilters: remaining.length === 0 ? undefined : {...complexFilters, filters: remaining}};
}

const agentViewDayPlanEventContainersValidator = zod.strictObject({
    eventContainers: zod.array(ottopilotEventContainerWithEventsValidator),
});

const buildAgentViewHeaders = ({
    accessToken,
    workspaceId,
}: {
    accessToken: string;
    workspaceId: Uuid;
}) => ({
    Authorization: `Bearer ${accessToken}`,
    "Content-Type": "application/json",
    "X-Workspace-Id": workspaceId,
});

export async function getAgentViewDayPlanEventContainers({
    accessToken,
    complexFilters,
    leadCategory,
    limit,
    offset,
    intellsysBaseUrl,
    workspaceId,
}: {
    accessToken: string;
    complexFilters?: PlutoComplexFilter;
    leadCategory?: AgentViewQueueDisplayGroup;
    limit: Integer;
    offset: Integer;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
}): Promise<Array<EventContainerWithEvents>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/agent-view/day-plan/event-containers/get`,
        {
            method: "POST",
            headers: buildAgentViewHeaders({accessToken, workspaceId}),
            body: JSON.stringify({
                ...resolveAgentViewQueueFilters(complexFilters),
                ...(leadCategory === undefined ? {} : {leadCategory}),
                limit: limit,
                offset: offset,
            }),
        },
        agentViewDayPlanEventContainersValidator,
    );

    if (result.success !== true) {
        throw result.error;
    }

    return result.data.eventContainers;
}

export async function countAgentViewDayPlanEventContainers({
    accessToken,
    complexFilters,
    leadCategory,
    intellsysBaseUrl,
    workspaceId,
}: {
    accessToken: string;
    complexFilters?: PlutoComplexFilter;
    leadCategory?: AgentViewQueueDisplayGroup;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
}): Promise<Integer> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/agent-view/day-plan/event-containers/count`,
        {
            method: "POST",
            headers: buildAgentViewHeaders({accessToken, workspaceId}),
            body: JSON.stringify({
                ...resolveAgentViewQueueFilters(complexFilters),
                ...(leadCategory === undefined ? {} : {leadCategory}),
            }),
        },
        zod.number(),
    );

    if (result.success !== true) {
        throw result.error;
    }

    return result.data as Integer;
}

export async function getAgentViewDayPlanEventContainerColumnValues({
    accessToken,
    column,
    complexFilters,
    leadCategory,
    intellsysBaseUrl,
    workspaceId,
}: {
    accessToken: string;
    column: string;
    complexFilters?: PlutoComplexFilter;
    leadCategory?: AgentViewQueueDisplayGroup;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
}): Promise<Array<string>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/agent-view/day-plan/event-containers/column-values`,
        {
            method: "POST",
            headers: buildAgentViewHeaders({accessToken, workspaceId}),
            body: JSON.stringify({
                column: column,
                ...resolveAgentViewQueueFilters(complexFilters),
                ...(leadCategory === undefined ? {} : {leadCategory}),
            }),
        },
        columnValuesFromApiZodValidator,
    );

    if (result.success !== true) {
        throw result.error;
    }

    return result.data;
}
